import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { whatsappLink, googleMapsLink } from '@/mocks/gymInfo';

export default function HeroSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.hero-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 120}ms`;
            target.classList.add('hero-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden pt-20 md:pt-24">
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          title={t('home.hero.videoTitle')}
          aria-label={t('home.hero.videoAriaLabel')}
          poster="https://storage.readdy-site.link/project_files/1a1c8768-7964-48a6-af85-b4ce5b39e023/edfbf516-fa9d-486d-87f9-9f52c9c1de2b_topgym-estrutura1.webp?v=52881ca8ffdce80104f87fedcc2d51ca"
          className="w-full h-full object-cover"
        >
          <source src="https://storage.readdy-site.link/project_files/1a1c8768-7964-48a6-af85-b4ce5b39e023/8f0775cb-28ff-47d9-b22f-fcc18090287c_TopGym-Drone.mp4?v=a305826b0dda587592cf473734be4a66" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/30 to-foreground-950/60"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 py-10 md:py-16">
        <div className="max-w-3xl md:text-left text-center">
          <h1 className="hero-reveal font-heading text-5xl md:text-7xl lg:text-8xl text-background-50 leading-none tracking-wide text-center md:text-left">
            {t('home.hero.title1')}
            <br />
            {t('home.hero.title2')}
          </h1>
          <p className="hero-reveal font-body text-base md:text-lg text-background-100/80 mt-6 max-w-lg leading-relaxed text-center md:text-left mx-auto md:mx-0">
            {t('home.hero.subtitle')}
          </p>
          <div className="hero-reveal flex flex-col sm:flex-row gap-3 mt-8 justify-center md:justify-start">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hover inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 font-body text-sm font-semibold px-6 py-3.5 rounded-full whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base"></i>
              {t('home.hero.ctaWhatsApp')}
            </a>
            <a
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hover inline-flex items-center justify-center gap-2 border border-background-50/60 hover:border-background-50 text-background-50 font-body text-sm font-medium px-6 py-3.5 rounded-full whitespace-nowrap"
            >
              <i className="ri-map-pin-line text-base"></i>
              {t('home.hero.ctaMap')}
            </a>
          </div>
          <div className="hero-reveal flex justify-center w-full mt-8 md:absolute md:bottom-8 md:right-6 md:mt-0 md:w-auto md:z-10">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hover w-28 h-28 md:w-32 md:h-32 bg-primary-500 hover:bg-primary-600 text-foreground-950 rounded-full flex flex-col items-center justify-center p-2 transition-transform hover:scale-105"
            >
              <span className="font-body font-bold leading-tight text-center">
                <span className="block text-[10px] md:text-[11px] font-normal opacity-80">{t('home.hero.priceBadgeLine1')}</span>
                <span className="block text-sm md:text-lg">{t('home.hero.priceBadgeLine2')}</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
